/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2021 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 *
 * @file ResourceMenuContainer.jsx
 * @author Srinivasan Bakthavatchalam
 * @since 1.0.1
 */

import React, { Fragment, memo } from 'react';
import PropTypes from 'prop-types';
import IconButton from '@material-ui/core/IconButton';
import { Icon } from '@greenville/mui-theme';
import { withStyles } from '@material-ui/core/styles';

import BookAndPageLevelTab from '../components/tabs/BookAndPageLevelTab';
import ResourceUtils from '../utils/ResourceUtils';

const styles = (theme) => ({
  menuBtn: {
    background: theme.color.background,
    border: `1px solid ${theme.color.divider}`,
    height: 36,
    width: 36,
    '&:hover': {
      backgroundColor: theme.color.background
    },
    '& svg': {
      color: theme.color.textPrimary
    },
    '&.filterOn': {
      background: theme.color.textPrimary,
      '& svg': {
        color: theme.color.background
      }
    },
    '&.menuOn': {
      background: theme.color.textPrimary,
      '& svg': {
        color: theme.color.background
      }
    }
  }
});

const ResourceMenuContainer = ({
  bulkDownloadStatus,
  classes,
  handleFilterOn,
  handleSearchOn,
  handleTabChange,
  hasBookResource,
  hasPageResource,
  isCurrentTabEmpty,
  isFilterOn,
  isSearchOn,
  tabValue
}) => (
  <Fragment>
    <BookAndPageLevelTab
      bulkDownloadStatus={bulkDownloadStatus}
      handleTabChange={handleTabChange}
      hasBookResource={hasBookResource}
      hasPageResource={hasPageResource}
      tabValue={tabValue}
    />
    <div className="flex-grow" />
    <div className="resource-action-items">
      <IconButton
        className={`${classes.menuBtn} resource-action-button resource-action-filter ${isFilterOn ? 'filterOn' : 'filterOff'}`}
        disabled={isCurrentTabEmpty || ResourceUtils.isBulkDownloadInProgress(bulkDownloadStatus)}
        onClick={handleFilterOn}
      >
        <Icon
          color="action"
          name="FilterOptions"
          size="small"
        />
      </IconButton>
      <IconButton
        className={`${classes.menuBtn} ${isSearchOn ? 'menuOn' : 'menuOff'}`}
        disabled={isCurrentTabEmpty || ResourceUtils.isBulkDownloadInProgress(bulkDownloadStatus)}
        onClick={handleSearchOn}
      >
        <Icon
          color="action"
          name="Search"
          size="small"
        />
      </IconButton>
    </div>
  </Fragment>
);

ResourceMenuContainer.propTypes = {
  bulkDownloadStatus: PropTypes.string.isRequired,
  classes: PropTypes.object.isRequired,
  handleFilterOn: PropTypes.func.isRequired,
  handleSearchOn: PropTypes.func.isRequired,
  handleTabChange: PropTypes.func.isRequired,
  hasBookResource: PropTypes.bool.isRequired,
  hasPageResource: PropTypes.bool.isRequired,
  isCurrentTabEmpty: PropTypes.bool.isRequired,
  isFilterOn: PropTypes.bool.isRequired,
  isSearchOn: PropTypes.bool.isRequired,
  tabValue: PropTypes.number.isRequired
};

export default memo(withStyles(styles)(ResourceMenuContainer));
